import type { GetPathToEntityProps } from './types';

import { httpApiService } from 'app/api';
import { isSSR } from 'app/packages/components';
import { getBackendServerUrl } from 'app/ssr/utils';

import { ROUTE_ALIASES } from './constants';

const getPathSegments = (path: string) => {
  return path.split('/');
};

const getPathToEntity = ({ type, entity }: GetPathToEntityProps) => {
  return `${type}/sports/${entity.sportId}/unions/${entity.unionId}/tournaments/${entity.tournamentId}/stages/${entity.stageId}/matches/${entity.matchId}`;
};

export const getCurrentPath = (pathname: string) => {
  return pathname.startsWith('/') ? pathname.slice(1) : pathname;
};

const isTwoSegmentsInPath = (path: string) => getPathSegments(path).length <= 2;

export const isStaticAlias = (url: string, aliases: Record<string, string>) => {
  return url in aliases && aliases[url] !== url;
};

export const isDynamicAlias = (path: string) => {
  const segmentInPath = getPathSegments(path);
  const routeAliasesKeys = Object.keys(ROUTE_ALIASES);

  return isTwoSegmentsInPath(path) && routeAliasesKeys.includes(segmentInPath[0]);
};

export const isMatchPageAlias = (path: string) => {
  return isTwoSegmentsInPath(path) && path.startsWith('matches') && Number.isNaN(Number(path.slice(1)));
};

export const getUrlByDynamicAlias = (path: string) => {
  const segmentInPath = getPathSegments(path);

  return `${ROUTE_ALIASES[segmentInPath[0]]}/${segmentInPath[1]}`;
};

export const getMatchPageUrl = async (path: string) => {
  const matchId = getPathSegments(path)[1];
  const url = isSSR ? `${getBackendServerUrl()}/api/v1/preMatch/${matchId}` : `/api/v1/preMatch/${matchId}`;

  const response = await httpApiService
    .get(url)
    .catch((error) => error);

  if (response?.data?.status === 'found') {
    const matchData = response.data.payload.diff.matches[matchId];
    const entity = {
      sportId: matchData.sportId,
      unionId: matchData.tournamentUnionId,
      tournamentId: matchData.tournamentId,
      stageId: matchData.tournamentStageId,
      matchId: matchData.id,
    };

    return getPathToEntity({ type: 'prematch', entity });
  }

  if (response?.data?.status === 'live') {
    const matchData = response.data.payload;
    const entity = {
      sportId: matchData.sportId,
      unionId: matchData.unionId,
      tournamentId: matchData.tournamentId,
      stageId: matchData.stageId,
      matchId: matchData.matchId,
    };

    return getPathToEntity({ type: 'live', entity });
  }

  if (response?.data?.status === 'closed') {
    return '404';
  }

  return '404';
};
